/**
 * Recent Players Hook
 *
 * Provides access to recent players and handles persistence.
 * Filters out players older than 30 days.
 * @module hooks/useRecentPlayers
 */

import { useReactiveVar } from "@apollo/client";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useCallback, useEffect } from "react";

import {
  addRecentPlayer as addRecentPlayerToStore,
  removeRecentPlayer as removeRecentPlayerFromStore,
  RecentPlayer,
  recentPlayersVar,
} from "@/stores/recentPlayers";

const STORAGE_KEY = "@recentPlayers";

/** Remove recent players older than 30 days to prevent stale entries */
const RECENT_PLAYER_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Filters out recent players that have expired based on TTL
 * @param players - Recent players to filter
 * @returns Players that are still within the TTL window
 */
const filterExpiredPlayers = (
  players: readonly RecentPlayer[]
): readonly RecentPlayer[] => {
  const cutoff = Date.now() - RECENT_PLAYER_TTL_MS;
  return players.filter(p => p.viewedAt > cutoff);
};

/**
 * Hook for managing recent players with persistence.
 * Exposes addRecentPlayer for tracking views and removeRecentPlayer for
 * cleanup after a manual player is deleted so the "+" menu stays in sync.
 * Automatically filters out players older than 30 days.
 * @returns Object with recentPlayers array, addRecentPlayer, and removeRecentPlayer
 */
export const useRecentPlayers = () => {
  const rawRecentPlayers = useReactiveVar(recentPlayersVar);
  const recentPlayers = filterExpiredPlayers(rawRecentPlayers);
  const addRecentPlayer = useCallback(
    (player: Omit<RecentPlayer, "viewedAt">) => {
      addRecentPlayerToStore(player);
    },
    []
  );
  const removeRecentPlayer = useCallback((playerId: string) => {
    removeRecentPlayerFromStore(playerId);
  }, []);

  // Load from storage only on first mount (when reactive var is empty).
  // Prevents overwriting in-memory state when multiple components use this hook.
  useEffect(() => {
    const loadFromStorage = async () => {
      try {
        const currentPlayers = recentPlayersVar();
        if (currentPlayers.length > 0) return;

        const stored = await AsyncStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored) as readonly RecentPlayer[];
          recentPlayersVar(filterExpiredPlayers(parsed));
        }
      } catch {
        // Ignore storage errors
      }
    };
    loadFromStorage();
  }, []);

  // Save to storage whenever raw reactive var changes (stable reference)
  useEffect(() => {
    const saveToStorage = async () => {
      try {
        await AsyncStorage.setItem(
          STORAGE_KEY,
          JSON.stringify(rawRecentPlayers)
        );
      } catch {
        // Ignore storage errors
      }
    };
    if (rawRecentPlayers.length > 0) {
      saveToStorage();
    }
  }, [rawRecentPlayers]);

  return {
    recentPlayers,
    addRecentPlayer,
    removeRecentPlayer,
  };
};
